import { useCallback, useEffect, useRef, useState } from 'react';
import { AdminButton } from '@/components/admin/button';
import { Card, CardBody, CardHead } from '@/components/admin/card';
import { AToggle, FormRow } from '@/components/admin/form';
import { AdminIcon } from '@/components/admin/icon';
import { PageHead } from '@/components/admin/page-head';
import { Segmented } from '@/components/admin/segmented';
import { Tag } from '@/components/admin/tag';
import {
  messagingApi,
  type CustomerSearchItem,
  type NotificationLogItem,
  type NotificationTemplate,
  type WhatsappStatusResponse,
  type CustomerPreferenceResponse,
} from '@/lib/messaging/messaging.api';

const EVENT_OPTIONS = [
  { value: '', label: 'Tous les événements' },
  { value: 'ORDER_CREATED', label: 'ORDER_CREATED (Commande créée)' },
  { value: 'ORDER_CONFIRMED', label: 'ORDER_CONFIRMED (Commande confirmée)' },
  { value: 'ORDER_SHIPPED', label: 'ORDER_SHIPPED (Commande expédiée)' },
  { value: 'ORDER_DELIVERED', label: 'ORDER_DELIVERED (Commande livrée)' },
  { value: 'ORDER_CANCELLED', label: 'ORDER_CANCELLED (Commande annulée)' },
  { value: 'DELIVERY_ASSIGNED', label: 'DELIVERY_ASSIGNED (Tournée attribuée)' },
  { value: 'DELIVERY_FAILED', label: 'DELIVERY_FAILED (Avis de passage)' },
  { value: 'OTP_DRIVER', label: 'OTP_DRIVER (OTP Livreur)' },
  { value: 'OTP_CUSTOMER', label: 'OTP_CUSTOMER (OTP Client)' },
  { value: 'PAYMENT_SUCCESS', label: 'PAYMENT_SUCCESS (Paiement réussi)' },
  { value: 'CUSTOMER_ACTIVATION', label: 'CUSTOMER_ACTIVATION (Activation compte)' },
  { value: 'PASSWORD_RESET', label: 'PASSWORD_RESET (Réinitialisation MDP)' },
  { value: 'LOGIN_ALERT', label: 'LOGIN_ALERT (Alerte connexion)' },
  { value: 'TEAM_INVITATION', label: 'TEAM_INVITATION (Invitation équipe)' },
];

const COMMON_VARIABLES = [
  'client',
  'code',
  'numeroCommande',
  'tourCode',
  'stopCount',
  'driverName',
  'shipmentCode',
  'attemptCount',
  'montant',
  'date',
  'lien',
];

export function AdminNotificationsPage() {
  const [activeTab, setActiveTab] = useState<'templates' | 'logs' | 'preferences'>('templates');

  // WhatsApp status
  const [waStatus, setWaStatus] = useState<WhatsappStatusResponse | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState<boolean>(true);

  // Editor Modal / Drawer state
  const [editingTemplate, setEditingTemplate] = useState<Partial<NotificationTemplate> | null>(
    null,
  );
  const [isNew, setIsNew] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Preview & Test state
  const [previewVars] = useState<string>('{"client":"Kevin", "code":"482913", "numeroCommande":"CMD-2026-99"}');
  const [renderedPreview, setRenderedPreview] = useState<string>('');
  const [testRecipient, setTestRecipient] = useState<string>('');
  const [testSending, setTestSending] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  // Logs state
  const [logs, setLogs] = useState<NotificationLogItem[]>([]);
  const [logTotal, setLogTotal] = useState<number>(0);
  const [logTotalCost, setLogTotalCost] = useState<number>(0);
  const [logChannel, setLogChannel] = useState<string>('');
  const [logStatus, setLogStatus] = useState<string>('');
  const [logRecipient, setLogRecipient] = useState<string>('');
  const [loadingLogs, setLoadingLogs] = useState<boolean>(false);
  const [selectedLog, setSelectedLog] = useState<NotificationLogItem | null>(null);

  // Preferences (7.2) state
  const [prefCustomerId, setPrefCustomerId] = useState<string>('');
  const [prefCustomerLabel, setPrefCustomerLabel] = useState<string>(''); // Nom affiché
  const [prefSearchInput, setPrefSearchInput] = useState<string>('');
  const [prefSuggestions, setPrefSuggestions] = useState<CustomerSearchItem[]>([]);
  const [prefShowSuggestions, setPrefShowSuggestions] = useState<boolean>(false);
  const [prefSearching, setPrefSearching] = useState<boolean>(false);
  const [prefData, setPrefData] = useState<CustomerPreferenceResponse | null>(null);
  const [prefLoading, setPrefLoading] = useState<boolean>(false);
  const [prefSaving, setPrefSaving] = useState<string | null>(null);
  const [prefError, setPrefError] = useState<string | null>(null);
  const prefSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load WA Status
  useEffect(() => {
    messagingApi
      .getWhatsappStatus()
      .then(setWaStatus)
      .catch(() => setWaStatus({ configured: false, connected: false }));
  }, []);

  // Load Templates
  const loadTemplates = useCallback(() => {
    setLoadingTemplates(true);
    messagingApi
      .listTemplates(selectedEvent || undefined, selectedChannel || undefined)
      .then(setTemplates)
      .catch((err) => console.error(err))
      .finally(() => setLoadingTemplates(false));
  }, [selectedChannel, selectedEvent]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Load Logs
  const loadLogs = useCallback(() => {
    setLoadingLogs(true);
    messagingApi
      .listLogs({
        channel: logChannel || undefined,
        status: logStatus || undefined,
        recipient: logRecipient || undefined,
        limit: 50,
      })
      .then((res) => {
        setLogs(res.items);
        setLogTotal(res.total);
        setLogTotalCost(res.totalCost);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoadingLogs(false));
  }, [logChannel, logStatus, logRecipient]);

  // Le compteur de l'onglet affiche ce total : ne charger qu'à l'ouverture de
  // l'onglet faisait annoncer « Journal d'envois (0) » en permanence, y compris
  // quand des messages étaient bel et bien partis. On a cru pendant toute une
  // recette qu'aucune notification ne fonctionnait.
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Handle Edit/Preview
  useEffect(() => {
    if (editingTemplate?.content) {
      try {
        const parsed = JSON.parse(previewVars);
        messagingApi
          .previewTemplate(editingTemplate.content, editingTemplate.subject, parsed)
          .then((r) => setRenderedPreview(r.rendered))
          .catch(() => setRenderedPreview(editingTemplate.content || ''));
      } catch {
        setRenderedPreview(editingTemplate.content);
      }
    } else {
      setRenderedPreview('');
    }
  }, [editingTemplate?.content, editingTemplate?.subject, previewVars]);

  // Save template
  const handleSaveTemplate = async () => {
    if (
      !editingTemplate?.event ||
      !editingTemplate?.channel ||
      !editingTemplate?.name ||
      !editingTemplate?.content
    ) {
      setErrorMsg("Veuillez remplir l'événement, le canal, le nom et le contenu.");
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      if (isNew) {
        await messagingApi.createTemplate({
          event: editingTemplate.event,
          channel: editingTemplate.channel,
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          content: editingTemplate.content,
          isActive: editingTemplate.isActive ?? true,
          isEssential: editingTemplate.isEssential ?? false,
        });
      } else if (editingTemplate.id) {
        await messagingApi.updateTemplate(editingTemplate.id, {
          name: editingTemplate.name,
          subject: editingTemplate.subject,
          content: editingTemplate.content,
          isActive: editingTemplate.isActive,
          isEssential: editingTemplate.isEssential,
        });
      }
      setEditingTemplate(null);
      loadTemplates();
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const msg = errorObj.response?.data?.message || errorObj.message || 'Erreur lors de l\'enregistrement';
      setErrorMsg(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setSaving(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (id: string) => {
    if (!confirm('Voulez-vous vraiment supprimer ce modèle ?')) return;
    try {
      await messagingApi.deleteTemplate(id);
      loadTemplates();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  // Test send
  const handleTestSend = async () => {
    if (!editingTemplate?.id || !testRecipient) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const parsed = JSON.parse(previewVars);
      const res = await messagingApi.testTemplate(editingTemplate.id, testRecipient, parsed);
      setTestResult(
        res.sent
          ? `✅ Message envoyé avec succès via ${res.channel}`
          : `🛑 Message non remis (Canal bloqué par les préférences opt-out du client ou échec provider)`,
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setTestResult(`❌ Erreur: ${message}`);
    } finally {
      setTestSending(false);
    }
  };

  const insertVariable = (varName: string) => {
    if (!editingTemplate) return;
    setEditingTemplate({
      ...editingTemplate,
      content: `${editingTemplate.content || ''} {{${varName}}}`,
    });
  };

  return (
    <>
      <PageHead
        title="Bibliothèque & Notifications"
        sub="Modèles de messages, statut WhatsApp et journal d'envois"
        actions={
          <AdminButton
            onClick={() => {
              setIsNew(true);
              setEditingTemplate({
                event: 'ORDER_CONFIRMED',
                channel: 'sms',
                name: 'Nouveau modèle',
                content: 'Bonjour {{client}}, votre commande #{{numeroCommande}} est confirmée.',
                isActive: true,
                isEssential: false,
              });
              setErrorMsg(null);
            }}
          >
            <AdminIcon n="plus" s={14} />
            Nouveau Modèle
          </AdminButton>
        }
      />

      {/* WhatsApp Status Bar */}
      <Card style={{ marginBottom: 16 }}>
        <CardBody
          style={{
            padding: '12px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: waStatus?.connected ? 'var(--a-success-soft)' : 'var(--a-warning-soft)',
                display: 'grid',
                placeItems: 'center',
                color: waStatus?.connected ? 'var(--a-success)' : 'var(--a-warning)',
              }}
            >
              <AdminIcon n="chat" s={18} />
            </div>
            <div>
              <strong style={{ fontSize: 13 }}>
                WhatsApp Business Cloud API :{' '}
                {waStatus?.connected ? 'Connecté' : 'Non connecté / Mode Simulation'}
              </strong>
              <div className="muted" style={{ fontSize: 11 }}>
                {waStatus?.displayName
                  ? `${waStatus.displayName} (${waStatus.number}) · WABA ID: ${waStatus.wabaId}`
                  : waStatus?.reason || 'Mode simulation log actif'}
              </div>
            </div>
          </div>
          <Tag variant={waStatus?.connected ? 'success' : 'warning'}>
            {waStatus?.connected ? 'Meta Verified' : 'Mock Mode'}
          </Tag>
        </CardBody>
      </Card>

      {/* Tabs */}
      <div style={{ marginBottom: 16 }}>
        <Segmented
          options={[
            { value: 'templates', label: 'Bibliothèque de modèles' },
            { value: 'logs', label: `Journal d'envois (${logTotal})` },
            { value: 'preferences', label: 'Préférences client' },
          ]}
          value={activeTab}
          onChange={(val) => setActiveTab(val as 'templates' | 'logs' | 'preferences')}
        />
      </div>

      {activeTab === 'templates' && (
        <>
          {/* Filter Bar */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <Segmented
              options={[
                { value: '', label: 'Tous les canaux' },
                { value: 'sms', label: 'SMS' },
                { value: 'email', label: 'Email' },
                { value: 'whatsapp', label: 'WhatsApp' },
                { value: 'push', label: 'Push' },
              ]}
              value={selectedChannel}
              onChange={setSelectedChannel}
            />

            <select
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--a-border)',
                background: 'var(--a-bg-card)',
                fontSize: 12,
              }}
            >
              {EVENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Templates Grid / Table */}
          <Card>
            <CardBody style={{ padding: 0 }}>
              {loadingTemplates ? (
                <div style={{ padding: 24, textAlign: 'center' }} className="muted">
                  Chargement des modèles...
                </div>
              ) : templates.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center' }}>
                  <div className="muted" style={{ marginBottom: 8 }}>
                    Aucun modèle trouvé pour ces filtres.
                  </div>
                  <AdminButton
                    variant="ghost"
                    onClick={() => {
                      setSelectedChannel('');
                      setSelectedEvent('');
                    }}
                  >
                    Réinitialiser les filtres
                  </AdminButton>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr
                      style={{
                        borderBottom: '1px solid var(--a-border)',
                        background: 'var(--a-bg-soft)',
                        textAlign: 'left',
                      }}
                    >
                      <th
                        style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600 }}
                        className="muted"
                      >
                        ÉVÉNEMENT
                      </th>
                      <th
                        style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600 }}
                        className="muted"
                      >
                        CANAL / NOM
                      </th>
                      <th
                        style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600 }}
                        className="muted"
                      >
                        CONTENU / APERÇU
                      </th>
                      <th
                        style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600 }}
                        className="muted"
                      >
                        STATUT
                      </th>
                      <th
                        style={{
                          padding: '10px 16px',
                          textAlign: 'right',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                        className="muted"
                      >
                        ACTIONS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((tpl) => (
                      <tr key={tpl.id} style={{ borderBottom: '1px solid var(--a-border)' }}>
                        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                          <strong style={{ display: 'block', fontSize: 12 }}>{tpl.event}</strong>
                          {tpl.isEssential && (
                            <Tag variant="danger" style={{ marginTop: 4 }}>
                              Essentiel
                            </Tag>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tag
                              variant={
                                tpl.channel === 'sms'
                                  ? 'info'
                                  : tpl.channel === 'whatsapp'
                                    ? 'success'
                                    : tpl.channel === 'email'
                                      ? 'terra'
                                      : 'neutral'
                              }
                            >
                              {tpl.channel.toUpperCase()}
                            </Tag>
                            <span style={{ fontWeight: 600 }}>{tpl.name}</span>
                          </div>
                          {tpl.subject && (
                            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                              Objet: {tpl.subject}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'top', maxWidth: 360 }}>
                          <div
                            style={{
                              fontSize: 12,
                              background: 'var(--a-bg-soft)',
                              padding: '6px 10px',
                              borderRadius: 6,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'monospace',
                            }}
                          >
                            {tpl.content}
                          </div>
                          {tpl.variables?.length > 0 && (
                            <div
                              style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}
                            >
                              {tpl.variables.map((v) => (
                                <span
                                  key={v}
                                  style={{
                                    fontSize: 10,
                                    background: 'var(--a-border-soft)',
                                    padding: '1px 5px',
                                    borderRadius: 4,
                                  }}
                                >
                                  {`{{${v}}}`}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', verticalAlign: 'top' }}>
                          <Tag variant={tpl.isActive ? 'success' : 'neutral'}>
                            {tpl.isActive ? 'Actif' : 'Inactif'}
                          </Tag>
                        </td>
                        <td
                          style={{ padding: '12px 16px', textAlign: 'right', verticalAlign: 'top' }}
                        >
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <AdminButton
                              variant="ghost"
                              onClick={() => {
                                setIsNew(false);
                                setEditingTemplate(tpl);
                                setErrorMsg(null);
                                setTestResult(null);
                              }}
                            >
                              <AdminIcon n="edit" s={14} />
                              Éditer
                            </AdminButton>
                            <AdminButton
                              variant="ghost"
                              onClick={() => handleDeleteTemplate(tpl.id)}
                            >
                              <AdminIcon n="trash" s={14} />
                            </AdminButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {activeTab === 'logs' && (
        <>
          {/* Logs Filters */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginBottom: 16,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Segmented
              options={[
                { value: '', label: 'Tous les canaux' },
                { value: 'sms', label: 'SMS' },
                { value: 'email', label: 'Email' },
                { value: 'whatsapp', label: 'WhatsApp' },
              ]}
              value={logChannel}
              onChange={setLogChannel}
            />

            <Segmented
              options={[
                { value: '', label: 'Tous les statuts' },
                { value: 'sent', label: 'Envoyés' },
                { value: 'failed', label: 'Échecs' },
                { value: 'skipped_optout', label: 'Bloqués Opt-out' },
              ]}
              value={logStatus}
              onChange={setLogStatus}
            />

            <input
              type="text"
              placeholder="Rechercher par destinataire..."
              value={logRecipient}
              onChange={(e) => setLogRecipient(e.target.value)}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--a-border)',
                fontSize: 12,
                minWidth: 200,
              }}
            />

            <div style={{ marginLeft: 'auto', fontSize: 12 }} className="muted">
              Coût total estimé : <strong>{logTotalCost.toLocaleString()} FCFA</strong>
            </div>
          </div>

          <Card>
            <CardBody style={{ padding: 0 }}>
              {loadingLogs ? (
                <div style={{ padding: 24, textAlign: 'center' }} className="muted">
                  Chargement de l'historique...
                </div>
              ) : logs.length === 0 ? (
                <div style={{ padding: 32, textAlign: 'center' }} className="muted">
                  Aucun log d'envoi enregistré.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr
                      style={{
                        borderBottom: '1px solid var(--a-border)',
                        background: 'var(--a-bg-soft)',
                        textAlign: 'left',
                      }}
                    >
                      <th style={{ padding: '10px 16px', fontSize: 11 }} className="muted">
                        DATE / HEURE
                      </th>
                      <th style={{ padding: '10px 16px', fontSize: 11 }} className="muted">
                        DESTINATAIRE
                      </th>
                      <th style={{ padding: '10px 16px', fontSize: 11 }} className="muted">
                        CANAL / ÉVÉNEMENT
                      </th>
                      <th style={{ padding: '10px 16px', fontSize: 11 }} className="muted">
                        STATUT
                      </th>
                      <th style={{ padding: '10px 16px', fontSize: 11 }} className="muted">
                        COÛT
                      </th>
                      <th
                        style={{ padding: '10px 16px', textAlign: 'right', fontSize: 11 }}
                        className="muted"
                      >
                        DÉTAILS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid var(--a-border)' }}>
                        <td style={{ padding: '10px 16px', fontSize: 12 }} className="mono muted">
                          {new Date(log.createdAt).toLocaleString('fr-FR')}
                        </td>
                        <td style={{ padding: '10px 16px', fontWeight: 600 }}>{log.recipient}</td>
                        <td style={{ padding: '10px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Tag variant={log.channel === 'sms' ? 'info' : log.channel === 'whatsapp' ? 'success' : 'terra'}>
                              {log.channel.toUpperCase()}
                            </Tag>
                            <span>{log.event || 'Spontané'}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 16px' }}>
                          <Tag variant={log.status === 'sent' ? 'success' : log.status === 'skipped_optout' ? 'warning' : 'danger'}>
                            {log.status}
                          </Tag>
                        </td>
                        <td style={{ padding: '10px 16px', fontSize: 12 }}>{log.cost} F</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                          <AdminButton variant="ghost" onClick={() => setSelectedLog(log)}>
                            Voir
                          </AdminButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Template Editor Drawer / Modal */}
      {editingTemplate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: '#ffffff',
              color: 'var(--a-ink)',
              border: '1px solid var(--a-border)',
              width: '90%',
              maxWidth: 700,
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 12,
              padding: 24,
              boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 20,
              }}
            >
              <h3 >
                {isNew ? 'Nouveau modèle de message' : 'Modifier le modèle'}
              </h3>
              <button
                type="button"
                onClick={() => setEditingTemplate(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}
              >
                ✕
              </button>
            </div>

            {errorMsg && (
              <div
                style={{
                  padding: '10px 14px',
                  background: 'var(--a-danger-soft)',
                  color: 'var(--a-danger)',
                  borderRadius: 8,
                  marginBottom: 16,
                  fontSize: 12,
                }}
              >
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormRow
                label="Événement déclencheur"
                required
                helper={!isNew ? 'Non modifiable en édition' : undefined}
              >
                <select
                  disabled={!isNew}
                  value={editingTemplate.event || ''}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, event: e.target.value })
                  }
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--a-border)',
                    background: !isNew ? 'var(--a-bg-soft)' : 'var(--a-bg-card)',
                    color: 'var(--a-text)',
                    cursor: !isNew ? 'not-allowed' : 'pointer',
                  }}
                >
                  {EVENT_OPTIONS.filter((o) => o.value !== '').map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </FormRow>

              <FormRow
                label="Canal de communication"
                required
                helper={!isNew ? 'Non modifiable en édition' : undefined}
              >
                <select
                  disabled={!isNew}
                  value={editingTemplate.channel || ''}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, channel: e.target.value })
                  }
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--a-border)',
                    background: !isNew ? 'var(--a-bg-soft)' : 'var(--a-bg-card)',
                    color: 'var(--a-text)',
                    cursor: !isNew ? 'not-allowed' : 'pointer',
                  }}
                >
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="email">Email</option>
                  <option value="push">Push</option>
                </select>
              </FormRow>
            </div>

            <FormRow label="Nom lisible" required>
              <input
                type="text"
                value={editingTemplate.name || ''}
                onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                placeholder="ex: Confirmation de commande SMS"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--a-border)',
                }}
              />
            </FormRow>

            {editingTemplate.channel === 'email' && (
              <FormRow label="Objet de l'email">
                <input
                  type="text"
                  value={editingTemplate.subject || ''}
                  onChange={(e) =>
                    setEditingTemplate({ ...editingTemplate, subject: e.target.value })
                  }
                  placeholder="ex: ✅ Votre commande #{{numeroCommande}} est confirmée !"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--a-border)',
                  }}
                />
              </FormRow>
            )}

            <FormRow label="Contenu du message avec variables {{variable}}" required>
              <div style={{ marginBottom: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 11 }}>
                  Insérer variable :
                </span>
                {COMMON_VARIABLES.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => insertVariable(v)}
                    style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      border: '1px solid var(--a-border)',
                      background: 'var(--a-bg-soft)',
                      cursor: 'pointer',
                    }}
                  >
                    + {v}
                  </button>
                ))}
              </div>
              <textarea
                rows={4}
                value={editingTemplate.content || ''}
                onChange={(e) =>
                  setEditingTemplate({ ...editingTemplate, content: e.target.value })
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--a-border)',
                  fontFamily: 'monospace',
                }}
              />
            </FormRow>

            {/* Live Preview Section */}
            <Card style={{ marginBottom: 16, background: 'var(--a-bg-soft)' }}>
              <CardHead title="Aperçu du rendu en temps réel" />
              <CardBody style={{ fontSize: 12 }}>
                {renderedPreview ? (
                  <div
                    style={{
                      background: 'var(--a-bg-card)',
                      padding: 12,
                      borderRadius: 8,
                      border: '1px solid var(--a-border)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {renderedPreview}
                  </div>
                ) : (
                  <span className="muted">Saisissez un contenu pour voir l'aperçu...</span>
                )}
              </CardBody>
            </Card>

            {/* Test Send Box (only for existing template) */}
            {!isNew && editingTemplate.id && (
              <Card style={{ marginBottom: 16, border: '1px dashed var(--a-border)' }}>
                <CardHead title="Tester l'envoi du message" />
                <CardBody style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Téléphone / Email destinataire..."
                    value={testRecipient}
                    onChange={(e) => setTestRecipient(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--a-border)',
                      fontSize: 12,
                    }}
                  />
                  <AdminButton onClick={handleTestSend} disabled={testSending || !testRecipient}>
                    {testSending ? 'Envoi...' : 'Envoyer test'}
                  </AdminButton>
                </CardBody>
                {testResult && (
                  <div style={{ padding: '0 16px 12px', fontSize: 12 }}>{testResult}</div>
                )}
              </Card>
            )}

            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <AToggle
                  on={editingTemplate.isActive ?? true}
                  onChange={(val) => setEditingTemplate({ ...editingTemplate, isActive: val })}
                />
                <span style={{ fontSize: 12, fontWeight: 600 }}>Template actif</span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 24 }}>
                <AToggle
                  on={editingTemplate.isEssential ?? false}
                  onChange={(val) => setEditingTemplate({ ...editingTemplate, isEssential: val })}
                />
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  Message essentiel (Ignore opt-out client)
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <AdminButton variant="ghost" onClick={() => setEditingTemplate(null)}>
                Annuler
              </AdminButton>
              <AdminButton onClick={handleSaveTemplate} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </AdminButton>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Tab (7.2) */}
      {activeTab === 'preferences' && (
        <>
          <Card style={{ marginBottom: 16 }}>
            <CardHead
              title="Préférences de contact par client"
              sub="Recherchez un client et pilotez ses canaux de communication autorisés"
            />
            <CardBody>
              {/* Barre de recherche autocomplete */}
              <div style={{ position: 'relative', marginBottom: 20 }}>
                <FormRow label="Rechercher un client (nom, email, téléphone)">
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="ex: Sandra, tagouffo@..., +237..."
                      value={prefSearchInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPrefSearchInput(val);
                        setPrefData(null);
                        setPrefCustomerId('');
                        setPrefCustomerLabel('');
                        setPrefError(null);
                        if (prefSearchRef.current) clearTimeout(prefSearchRef.current);
                        if (val.trim().length < 2) {
                          setPrefSuggestions([]);
                          setPrefShowSuggestions(false);
                          return;
                        }
                        prefSearchRef.current = setTimeout(() => {
                          setPrefSearching(true);
                          messagingApi
                            .searchCustomers(val.trim())
                            .then((res) => {
                              setPrefSuggestions(res.items);
                              setPrefShowSuggestions(res.items.length > 0);
                            })
                            .catch(() => setPrefSuggestions([]))
                            .finally(() => setPrefSearching(false));
                        }, 300);
                      }}
                      onBlur={() => setTimeout(() => setPrefShowSuggestions(false), 200)}
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 14px',
                        borderRadius: 8,
                        border: '1px solid var(--a-border)',
                        background: 'var(--a-bg-card)',
                        color: 'var(--a-text)',
                        fontSize: 13,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--a-text-muted)',
                      }}
                    >
                      {prefSearching ? '⏳' : <AdminIcon n="search" s={14} />}
                    </div>
                    {prefShowSuggestions && prefSuggestions.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: 'var(--a-bg-card)',
                          border: '1px solid var(--a-border)',
                          borderRadius: 8,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          zIndex: 999,
                          maxHeight: 240,
                          overflowY: 'auto',
                          marginTop: 4,
                        }}
                      >
                        {prefSuggestions.map((customer) => {
                          const name =
                            [customer.firstName, customer.lastName].filter(Boolean).join(' ') ||
                            customer.email ||
                            customer.phone ||
                            'Client sans nom';
                          return (
                            <button
                              key={customer.id}
                              onClick={() => {
                                setPrefSearchInput(name);
                                setPrefCustomerLabel(name);
                                setPrefCustomerId(customer.id);
                                setPrefShowSuggestions(false);
                                setPrefLoading(true);
                                setPrefError(null);
                                messagingApi
                                  .getCustomerPreferences(customer.id)
                                  .then(setPrefData)
                                  .catch(() =>
                                    setPrefError(
                                      'Impossible de charger les préférences de ce client.',
                                    ),
                                  )
                                  .finally(() => setPrefLoading(false));
                              }}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                padding: '10px 14px',
                                background: 'none',
                                border: 'none',
                                borderBottom: '1px solid var(--a-border)',
                                cursor: 'pointer',
                                textAlign: 'left',
                              }}
                            >
                              <span style={{ fontWeight: 600, fontSize: 13 }}>{name}</span>
                              <span style={{ fontSize: 11, color: 'var(--a-text-muted)' }}>
                                {[customer.email, customer.phone].filter(Boolean).join(' · ')}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </FormRow>
                {prefLoading && (
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--a-text-muted)' }}>
                    Chargement des préférences...
                  </div>
                )}
              </div>

              {prefError && (
                <div style={{ color: 'var(--a-danger)', fontSize: 13, marginBottom: 16 }}>
                  ❌ {prefError}
                </div>
              )}

              {prefData && (
                <>
                  <div
                    style={{
                      marginBottom: 12,
                      fontSize: 12,
                      color: 'var(--a-text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <AdminIcon n="users" s={14} />
                    <strong style={{ color: 'var(--a-text)', fontSize: 13 }}>
                      {prefCustomerLabel || 'Client'}
                    </strong>
                  </div>
                  <div style={{ display: 'grid', gap: 10 }}>
                    {(['sms', 'whatsapp', 'email', 'push'] as const).map((canal) => {
                      const enabled = prefData[canal] !== false;
                      return (
                        <div
                          key={canal}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            borderRadius: 8,
                            border: '1px solid var(--a-border)',
                            background: enabled ? 'var(--a-bg-card)' : 'var(--a-bg-soft)',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Tag variant={enabled ? 'success' : 'danger'}>{canal.toUpperCase()}</Tag>
                            <span style={{ fontSize: 13, fontWeight: 500 }}>
                              {canal === 'sms' && 'Messages SMS'}
                              {canal === 'whatsapp' && 'Messages WhatsApp'}
                              {canal === 'email' && 'Emails transactionnels'}
                              {canal === 'push' && 'Notifications Push'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {prefSaving === canal && (
                              <span style={{ fontSize: 11, color: 'var(--a-text-muted)' }}>
                                Sauvegarde...
                              </span>
                            )}
                            <AToggle
                              on={enabled}
                              onChange={async (val) => {
                                setPrefSaving(canal);
                                try {
                                  const updated = await messagingApi.updateCustomerPreferences(
                                    prefCustomerId,
                                    { [canal]: val },
                                  );
                                  setPrefData(updated);
                                } catch {
                                  setPrefError(`Erreur lors de la mise à jour du canal ${canal}.`);
                                } finally {
                                  setPrefSaving(null);
                                }
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: 16,
                      padding: '10px 14px',
                      background: 'var(--a-bg-soft)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--a-text-muted)',
                    }}
                  >
                    ℹ️ Les canaux désactivés ne reçoivent plus les messages non-essentiels. Les
                    messages essentiels (OTP, sécurité) sont toujours transmis.
                  </div>
                </>
              )}

              {!prefData && !prefLoading && !prefError && (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '32px 0',
                    color: 'var(--a-text-muted)',
                    fontSize: 13,
                  }}
                >
                  <AdminIcon n="users" s={32} />
                  <p>Saisissez un identifiant client (UUID) et cliquez sur Rechercher.</p>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: '#ffffff',
              color: 'var(--a-ink)',
              border: '1px solid var(--a-border)',
              boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
              width: '90%',
              maxWidth: 550,
              borderRadius: 12,
              padding: 24,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Détail de la notification</h3>
              <AdminButton variant="ghost" onClick={() => setSelectedLog(null)}>
                Fermer
              </AdminButton>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, overflowY: 'auto' }}>
              <div>
                <strong>Destinataire :</strong> {selectedLog.recipient}
              </div>
              <div>
                <strong>Canal / Événement :</strong> {selectedLog.channel.toUpperCase()} /{' '}
                {selectedLog.event || 'N/A'}
              </div>
              <div>
                <strong>Date d'envoi :</strong>{' '}
                {new Date(selectedLog.createdAt).toLocaleString('fr-FR')}
              </div>
              <div>
                <strong>Statut :</strong>{' '}
                <Tag variant={selectedLog.status === 'sent' ? 'success' : selectedLog.status === 'skipped_optout' ? 'warning' : 'danger'}>
                  {selectedLog.status}
                </Tag>
              </div>
              {selectedLog.subject && (
                <div>
                  <strong>Sujet :</strong> {selectedLog.subject}
                </div>
              )}
              {selectedLog.body && (
                <div>
                  <strong>Corps du message rendu :</strong>
                  <pre
                    style={{
                      background: 'var(--a-bg-soft)',
                      padding: 10,
                      borderRadius: 6,
                      fontSize: 12,
                      whiteSpace: 'pre-wrap',
                      marginTop: 4,
                    }}
                  >
                    {selectedLog.body}
                  </pre>
                </div>
              )}
              {selectedLog.errorMessage && (
                <div style={{ color: 'var(--a-danger)' }}>
                  <strong>Erreur :</strong> {selectedLog.errorMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
